import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/appConstants';

/**
 * Task Estimate status values
 */
export type TaskEstimateStatus = 
  | 'pending' 
  | 'approved' 
  | 'rejected' 
  | 'in_progress' 
  | 'approaching' 
  | 'exceeded' 
  | 'completed';

/**
 * Task Estimate data structure
 */
export interface TaskEstimate {
  id: number;
  intranet_task_id: string | null;
  monday_task_id: string | null;
  task_description: string;
  task_type: string;
  project_id: string | null;
  project_name: string | null;
  account_id: string | null;
  account_name: string | null;
  employee_id: string;
  employee_name: string | null;
  employee_email: string | null;
  estimate_hours: number;
  estimate_notes: string | null;
  status: TaskEstimateStatus;
  approved_by_id: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  approval_notes: string | null;
  time_spent_seconds: number;
  work_started_at: string | null;
  work_completed_at: string | null;
  approaching_alert_sent: boolean;
  exceeded_alert_sent: boolean;
  approaching_threshold_percent: number;
  created_at: string;
  updated_at: string;
}

/**
 * Data for submitting a new estimate
 */
export interface SubmitEstimateData {
  taskDescription: string;
  taskType: string;
  projectId?: string;
  projectName?: string;
  accountId?: string;
  accountName?: string;
  employeeId: string;
  employeeName: string;
  estimateHours: number;
  estimateNotes?: string;
  mondayTaskId?: string;
  approachingThresholdPercent?: number;
}

interface UseTaskEstimatesResult {
  // My estimates (for developers)
  myEstimates: TaskEstimate[];
  loadingMy: boolean;
  
  // Pending estimates (for managers)
  pendingEstimates: TaskEstimate[];
  loadingPending: boolean;
  
  // Actions
  submitEstimate: (data: SubmitEstimateData) => Promise<{ success: boolean; estimate?: TaskEstimate; message?: string }>;
  approveEstimate: (id: number, approverId: string, approverName: string, notes?: string) => Promise<{ success: boolean; message?: string }>;
  rejectEstimate: (id: number, approverId: string, approverName: string, reason: string) => Promise<{ success: boolean; message?: string }>;
  startWork: (id: number, intranetTaskId?: string) => Promise<{ success: boolean; message?: string }>;
  deleteEstimate: (id: number, employeeId: string) => Promise<{ success: boolean; message?: string }>;
  rerequestApproval: (id: number, employeeId: string, employeeName: string, newEstimateHours?: number, newEstimateNotes?: string) => Promise<{ success: boolean; message?: string }>;
  resendNotification: (id: number, employeeId: string, employeeName: string) => Promise<{ success: boolean; message?: string }>;
  
  // Refresh
  refreshMyEstimates: () => Promise<void>;
  refreshPendingEstimates: () => Promise<void>;
  
  // Helpers
  getApprovedEstimate: (employeeId: string) => TaskEstimate | undefined;
  hasApprovedEstimateForTask: (taskDescription: string, employeeId: string) => TaskEstimate | undefined;
  getRejectedEstimates: (employeeId: string) => TaskEstimate[];
  
  error: string | null;
}

/**
 * Hook for managing task estimates workflow
 * @param employeeId - Current user's employee ID
 * @param isManager - Whether the user is a manager (can approve/reject)
 */
export const useTaskEstimates = (employeeId?: string, isManager: boolean = false): UseTaskEstimatesResult => {
  const [myEstimates, setMyEstimates] = useState<TaskEstimate[]>([]);
  const [pendingEstimates, setPendingEstimates] = useState<TaskEstimate[]>([]);
  const [loadingMy, setLoadingMy] = useState(false);
  const [loadingPending, setLoadingPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Compare two estimate arrays to see if they're meaningfully different
   */
  const estimatesChanged = useCallback((prev: TaskEstimate[], next: TaskEstimate[]): boolean => {
    if (prev.length !== next.length) return true;
    
    for (let i = 0; i < prev.length; i++) {
      const p = prev[i];
      const n = next.find(e => e.id === p.id);
      if (!n) return true;
      // Check key fields that would require a re-render
      if (p.status !== n.status || 
          p.time_spent_seconds !== n.time_spent_seconds ||
          p.approved_at !== n.approved_at) {
        return true;
      }
    }
    return false;
  }, []);

  /**
   * Fetch my estimates (for developers)
   * @param showLoading - Whether to show loading state (false for background polling)
   */
  const refreshMyEstimates = useCallback(async (showLoading: boolean = true) => {
    if (!employeeId) return;
    
    if (showLoading) setLoadingMy(true);
    try {
      const response = await axios.get<TaskEstimate[]>(
        `${API_URL}/task-estimates/employee/${employeeId}`,
        { withCredentials: true }
      );
      // Only update state if data actually changed
      setMyEstimates(prev => {
        if (estimatesChanged(prev, response.data)) {
          return response.data;
        }
        return prev;
      });
      setError(null);
    } catch (err: any) {
      console.error('Error fetching my estimates:', err);
      setError(err.response?.data?.error || 'Failed to fetch estimates');
    } finally {
      if (showLoading) setLoadingMy(false);
    }
  }, [employeeId, estimatesChanged]);

  /**
   * Fetch pending estimates (for managers)
   * @param showLoading - Whether to show loading state (false for background polling)
   */
  const refreshPendingEstimates = useCallback(async (showLoading: boolean = true) => {
    if (!isManager) return;
    
    if (showLoading) setLoadingPending(true);
    try {
      const response = await axios.get<TaskEstimate[]>(
        `${API_URL}/task-estimates/pending`,
        { withCredentials: true }
      );
      // Only update state if data actually changed
      setPendingEstimates(prev => {
        if (estimatesChanged(prev, response.data)) {
          return response.data;
        }
        return prev;
      });
      setError(null);
    } catch (err: any) {
      console.error('Error fetching pending estimates:', err);
      setError(err.response?.data?.error || 'Failed to fetch pending estimates');
    } finally {
      if (showLoading) setLoadingPending(false);
    }
  }, [isManager, estimatesChanged]);

  /**
   * Submit a new estimate for approval
   */
  const submitEstimate = useCallback(async (data: SubmitEstimateData): Promise<{ success: boolean; estimate?: TaskEstimate; message?: string }> => {
    try {
      const response = await axios.post<{ success: boolean; estimate: TaskEstimate; message: string }>(
        `${API_URL}/task-estimates`,
        data,
        { withCredentials: true }
      );
      
      // Refresh my estimates to include the new one
      await refreshMyEstimates();
      
      return { 
        success: true, 
        estimate: response.data.estimate,
        message: response.data.message 
      };
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to submit estimate';
      setError(message);
      return { success: false, message };
    }
  }, [refreshMyEstimates]);

  /**
   * Approve an estimate (manager action)
   */
  const approveEstimate = useCallback(async (
    id: number, 
    approverId: string, 
    approverName: string, 
    notes?: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await axios.put<{ success: boolean; message: string }>(
        `${API_URL}/task-estimates/${id}/approve`,
        { approvedById: approverId, approvedByName: approverName, approvalNotes: notes },
        { withCredentials: true }
      );
      
      // Refresh both pending estimates and user's own estimates
      await Promise.all([
        refreshPendingEstimates(),
        refreshMyEstimates()
      ]);
      
      return { success: true, message: response.data.message };
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to approve estimate';
      setError(message);
      return { success: false, message };
    }
  }, [refreshPendingEstimates, refreshMyEstimates]);

  /**
   * Reject an estimate (manager action)
   */
  const rejectEstimate = useCallback(async (
    id: number, 
    approverId: string, 
    approverName: string, 
    reason: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await axios.put<{ success: boolean; message: string }>(
        `${API_URL}/task-estimates/${id}/reject`,
        { approvedById: approverId, approvedByName: approverName, approvalNotes: reason },
        { withCredentials: true }
      );
      
      // Refresh both pending estimates and user's own estimates
      await Promise.all([
        refreshPendingEstimates(),
        refreshMyEstimates()
      ]);
      
      return { success: true, message: response.data.message };
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to reject estimate';
      setError(message);
      return { success: false, message };
    }
  }, [refreshPendingEstimates, refreshMyEstimates]);

  /**
   * Start work on an approved estimate
   */
  const startWork = useCallback(async (
    id: number, 
    intranetTaskId?: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await axios.put<{ success: boolean; message: string }>(
        `${API_URL}/task-estimates/${id}/start`,
        { intranetTaskId },
        { withCredentials: true }
      );
      
      // Refresh my estimates
      await refreshMyEstimates();
      
      return { success: true, message: response.data.message };
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to start work';
      setError(message);
      return { success: false, message };
    }
  }, [refreshMyEstimates]);

  /**
   * Delete an unstarted estimate
   */
  const deleteEstimate = useCallback(async (
    id: number, 
    employeeIdParam: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await axios.delete<{ success: boolean; message: string }>(
        `${API_URL}/task-estimates/${id}`,
        { 
          data: { employeeId: employeeIdParam },
          withCredentials: true 
        }
      );
      
      // Refresh my estimates
      await refreshMyEstimates();
      
      return { success: true, message: response.data.message };
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to delete estimate';
      setError(message);
      return { success: false, message };
    }
  }, [refreshMyEstimates]);

  /**
   * Re-request approval for a rejected estimate
   */
  const rerequestApproval = useCallback(async (
    id: number,
    employeeIdParam: string,
    employeeName: string,
    newEstimateHours?: number,
    newEstimateNotes?: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await axios.put<{ success: boolean; message: string }>(
        `${API_URL}/task-estimates/${id}/rerequest`,
        { 
          employeeId: employeeIdParam,
          employeeName,
          newEstimateHours,
          newEstimateNotes
        },
        { withCredentials: true }
      );
      
      // Refresh my estimates and pending estimates
      await refreshMyEstimates();
      if (isManager) {
        await refreshPendingEstimates();
      }
      
      return { success: true, message: response.data.message };
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to re-request approval';
      setError(message);
      return { success: false, message };
    }
  }, [refreshMyEstimates, refreshPendingEstimates, isManager]);

  /**
   * Resend Slack notification for a pending estimate
   */
  const resendNotification = useCallback(async (
    id: number,
    employeeIdParam: string,
    employeeName: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await axios.put<{ success: boolean; message: string }>(
        `${API_URL}/task-estimates/${id}/resend-notification`,
        { 
          employeeId: employeeIdParam,
          employeeName
        },
        { withCredentials: true }
      );
      
      return { success: true, message: response.data.message };
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to resend notification';
      setError(message);
      return { success: false, message };
    }
  }, []);

  /**
   * Get an approved estimate for the current user that's ready to start
   */
  const getApprovedEstimate = useCallback((empId: string): TaskEstimate | undefined => {
    return myEstimates.find(e => 
      e.employee_id === empId && 
      e.status === 'approved'
    );
  }, [myEstimates]);

  /**
   * Check if there's an approved estimate for a specific task description
   */
  const hasApprovedEstimateForTask = useCallback((taskDescription: string, empId: string): TaskEstimate | undefined => {
    return myEstimates.find(e => 
      e.employee_id === empId && 
      e.status === 'approved' &&
      e.task_description === taskDescription
    );
  }, [myEstimates]);

  /**
   * Get all rejected estimates for a user
   */
  const getRejectedEstimates = useCallback((empId: string): TaskEstimate[] => {
    return myEstimates.filter(e => 
      e.employee_id === empId && 
      e.status === 'rejected'
    );
  }, [myEstimates]);

  // Initial fetch
  useEffect(() => {
    if (employeeId) {
      refreshMyEstimates();
    }
  }, [employeeId, refreshMyEstimates]);

  useEffect(() => {
    if (isManager) {
      refreshPendingEstimates();
    }
  }, [isManager, refreshPendingEstimates]);

  // Auto-refresh estimates every 10 seconds to catch Slack approvals
  // Uses silent refresh (no loading indicator) to prevent UI flashing
  useEffect(() => {
    const interval = setInterval(() => {
      if (employeeId) {
        refreshMyEstimates(false); // Silent refresh
      }
      if (isManager) {
        refreshPendingEstimates(false); // Silent refresh
      }
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [employeeId, isManager, refreshMyEstimates, refreshPendingEstimates]);

  return {
    myEstimates,
    loadingMy,
    pendingEstimates,
    loadingPending,
    submitEstimate,
    approveEstimate,
    rejectEstimate,
    startWork,
    deleteEstimate,
    rerequestApproval,
    resendNotification,
    refreshMyEstimates,
    refreshPendingEstimates,
    getApprovedEstimate,
    hasApprovedEstimateForTask,
    getRejectedEstimates,
    error
  };
};

export default useTaskEstimates;

